/**
 * Diccionario es-CL (idioma por defecto del checkout). Sin dependencias: es un
 * objeto plano y su tipo literal define las claves validas de todo el widget, de
 * modo que `en.ts` no puede quedar incompleto sin romper la compilacion.
 */
export const esCL = {
  'common.pay': 'Pagar',
  'common.cancel': 'Cancelar',
  'common.retry': 'Reintentar',
  'common.back': 'Volver',
  /*
   * SUSTITUYE A `common.change_method`, que se fue el 2026-09-23 porque MENTIA.
   *
   * La usaba un solo boton --la salida de BCI-- y ese boton cancela: lleva a una pantalla terminal
   * cuya unica salida es volver a la tienda, sin selector al que volver. La vuelta al selector no se
   * construye a proposito (ver el boton en `BciHostedCheckoutFlow`), asi que la etiqueta que la
   * prometia no puede quedarse rondando el diccionario esperando a que alguien la reutilice.
   *
   * Y no vale `common.cancel` a secas: aqui hay que decir QUE se cancela, porque el comprador esta
   * mirando el formulario del banco y «Cancelar» a solas se lee como «cerrar este aviso».
   */
  'common.cancel_payment': 'Cancelar el pago',
  'common.secure_payment': 'Pago seguro',
  'common.powered_by': 'Procesado por ApiPay',
  // Etiqueta del carrusel de promociones. Es lo que anuncia un lector de pantalla al entrar en
  // el bloque, y lo que le permite saltarlo entero sabiendo que se salta.
  'promo.label': 'Promociones del comercio',

  'loading.session': 'Cargando tu pago...',

  'summary.title': 'Resumen del pago',
  'summary.total': 'Total a pagar',
  'summary.description': 'Detalle',
  'summary.merchant': 'Comercio',
  'summary.expires_at': 'Válido hasta {date}',
  // Desglose de lo que se paga, debajo del importe. «Detalle» y no «Resumen»: el resumen es toda
  // la tarjeta, y esto es lo de dentro.
  'summary.breakdown': 'Detalle del pago',
  'summary.units': '{quantity} x {unit}',
  'summary.item_count': '{count} productos',
  'summary.breakdown_scroll': 'Detalle del pago, desplazable',

  'method.title': 'Elige cómo pagar',
  'method.continue': 'Ir a pagar',
  'method.chosen': 'Pagarás con {method}',
  'method.select': 'Seleccionar',
  'method.webpay': 'Webpay Plus',
  'method.oneclick': 'Webpay Oneclick',
  'method.mercadopago': 'MercadoPago',
  'method.sandbox': 'Pasarela de pruebas',
  'method.globalgetnet': 'Global Getnet Web Checkout',
  'method.getnet': 'Getnet',
  'method.compraaqui': 'CompraAquí',
  'method.mach': 'MACH',
  'method.rutpay': 'RutPay',
  'method.santander': 'Botón de Pago Santander',
  // Es la tarjeta de BENEFICIO (alimentacion y colacion), no una tarjeta bancaria: el nombre
  // visible lo dice para que el comprador no la confunda con un medio de pago universal.
  'method.amipass': 'Amipass (tarjeta de beneficio)',
  'method.unknown': 'Otro medio de pago',

  'gateway.loading': 'Preparando el medio de pago...',
  'gateway.unavailable': 'Este medio de pago no está disponible ahora.',
  'gateway.redirect_cta': 'Pagar con {gateway}',
  'gateway.redirect_hint':
    'Te llevaremos al sitio de la pasarela para completar el pago de forma segura.',
  'gateway.redirecting': 'Te estamos redirigiendo...',
  // Traspaso automatico: el comercio fijo la pasarela, no hay nada que elegir y esta es la
  // UNICA frase que el comprador ve hasta aterrizar alli. Nombra el destino a proposito.
  'gateway.handoff': 'Te estamos llevando a {gateway}...',
  // Vuelta de una pasarela cuyo desenlace no viaja con el comprador, sino en su webhook.
  'gateway.return_hint':
    'Ya volviste de la pasarela. El resultado nos lo confirma ella directamente, así que esta pantalla se actualiza sola.',
  'gateway.returning': 'Estamos confirmando tu pago...',
  // RutPay en escritorio: el comprador escanea con su telefono y esta pantalla espera. No hay
  // vuelta del navegador que interpretar, asi que el desenlace llega por sondeo.
  'gateway.qr_hint': 'Escanea este código con la app de BancoEstado para pagar.',
  'gateway.qr_waiting': 'Esperando que completes el pago en tu teléfono...',
  'gateway.qr_alt': 'Código QR para pagar con {gateway}',
  'gateway.qr_unavailable': 'No pudimos mostrar el código QR. Intenta desde tu teléfono.',
  // Vuelta de una pasarela que SI publica consulta de estado: aqui el resultado lo preguntamos
  // nosotros, y por eso no se le promete al comprador que la pantalla se actualiza sola.
  'gateway.confirm_hint':
    'Ya volviste de la pasarela. Estamos comprobando con ella el resultado de tu pago.',
  'gateway.check_again': 'Comprobar de nuevo',
  // Formulario de la pasarela EMBEBIDO en esta pantalla (BCI). Las dos frases acompañan al
  // formulario de principio a fin, y por eso tienen que ser ciertas ANTES y DESPUÉS de pagar: la
  // primera promete vigilancia, no un desenlace, y la segunda no afirma que ya se pagó.
  'gateway.embedded_watching':
    'Estamos siguiendo tu pago. Esta pantalla se actualiza sola en cuanto tengamos el resultado; no cierres ni recargues la ventana.',
  // LA FRASE QUE DESACTIVA EL RECUADRO ROJO. El iframe de la pasarela reintenta por su cuenta y
  // puede pintar un error suyo —«Payment for this order has already been received»— DESPUÉS de un
  // pago correcto. Sin esta línea, eso es lo último que el comprador lee.
  'gateway.embedded_not_final':
    'Si el formulario de la pasarela muestra un aviso, no es el resultado final: el que vale te lo confirmamos aquí.',
  // EL RETO 3DS. El banco se apodera de la pantalla con una capa suya, y esta frase es lo único que
  // dice que esa capa la esperábamos: sin ella, al comprador le aparece encima una ventana de otro
  // sitio sin que nadie se la haya anunciado. Nombra al BANCO —no a la pasarela ni a ApiPay— porque
  // es quien pregunta, y pide que no cierre, que es lo que deja el cobro a medias.
  'gateway.embedded_3ds':
    'Tu banco está comprobando que eres tú. Completa la validación que aparece sobre esta pantalla; no cierres ni recargues la ventana.',

  // Oneclick: inscribir la tarjeta en Transbank y cobrar con ella al volver. El aviso dice
  // explicitamente donde se teclea la tarjeta, porque es la frontera de PCI DSS SAQ A y el
  // comprador tiene derecho a saber a quien se la esta dando.
  'oneclick.notice':
    'Primero inscribes tu tarjeta en el formulario de Transbank y con ella se cobra al volver. Los datos de la tarjeta se ingresan ÚNICAMENTE en el formulario de Transbank: ApiPay no los recibe ni los guarda.',
  'oneclick.cta': 'Inscribir tarjeta y pagar',
  'oneclick.opening': 'Preparando la inscripción con Transbank...',
  'oneclick.redirecting': 'Te estamos llevando al formulario de Transbank...',
  'oneclick.closing': 'Guardando tu tarjeta...',
  'oneclick.charging': 'Cobrando con tu tarjeta guardada...',
  'oneclick.no_form': 'Transbank no devolvió el formulario de inscripción. Intenta nuevamente.',

  'processing.title': 'Estamos confirmando tu pago',
  'processing.body': 'No cierres ni recargues esta ventana.',

  'requires_action.title': 'Necesitamos un paso más',
  'requires_action.body': 'Completa la verificación en la ventana de tu banco.',

  'status.back_to_merchant': 'Volver a la tienda',
  'status.auto_return': 'Volverás a la tienda en {segundos} s',
  'status.success.title': 'Pago realizado',
  'status.success.body': 'Tu pago fue aprobado correctamente.',
  'status.failed.title': 'No pudimos procesar tu pago',
  'status.failed.body': 'Revisa tu medio de pago o intenta con otro.',
  'status.expired.title': 'La sesión de pago expiró',
  'status.expired.body': 'Vuelve a la tienda y comienza el pago nuevamente.',
  // NO dice «no pudimos procesar tu pago»: sí se procesó, y después el comercio lo deshizo. Tampoco
  // promete un plazo para el dinero --lo decide el emisor de la tarjeta, no nosotros-- y por eso
  // remite a la referencia, que es lo único con lo que el comprador puede preguntar.
  'status.reversed.title': 'Este pago fue anulado',
  'status.reversed.body':
    'El comercio anuló este cobro. La devolución del monto la resuelve tu medio de pago; si necesitas consultarla, indica la referencia de abajo.',
  'status.blocked.title': 'No podemos mostrar el pago aquí',
  'status.blocked.body': 'Este checkout solo funciona en el sitio del comercio.',
  /*
   * EL TITULO FALTABA, Y SIN EL LA PANTALLA SE CONTRADECIA A SI MISMA.
   *
   * Existia el cuerpo y no existia esta clave, asi que el titulo caia al del rechazo: «No pudimos
   * procesar tu pago» encima de «Cancelaste el pago». Una nos echa la culpa y la otra dice que fue
   * el comprador, y la diferencia no es de estilo: quien lee eso decide si escribe a soporte por un
   * cobro que nadie ha roto. Visto en QA el 2026-09-23.
   *
   * Dice lo que paso y no lo repite el cuerpo, que sigue con lo que el comprador puede hacer ahora.
   */
  'status.canceled.title': 'Pago cancelado',
  'status.canceled.body': 'Cancelaste el pago. Puedes volver a intentarlo cuando quieras.',

  'error.title': 'Ocurrió un problema',
  // Etiqueta de la segunda linea del aviso: dice DE QUIEN es el texto que viene detras. El detalle
  // del servidor es tecnico —y puede venir en ingles o en espanol, segun que capa lo redacte—, asi
  // que el comprador tiene que poder reconocer de un vistazo que no va dirigido a el, sin necesidad
  // de entenderlo (ver ErrorMessage).
  'error.detail_label': 'Detalle del servidor, para el comercio:',
  'error.validation_error': 'Los datos del pago no son válidos.',
  'error.invalid_api_key': 'La configuración del comercio no es válida.',
  'error.invalid_client_secret': 'Esta sesión de pago ya no es válida.',
  'error.insufficient_permissions': 'Esta sesión de pago no tiene permisos para continuar.',
  'error.resource_not_found': 'No encontramos esta sesión de pago.',
  'error.card_declined': 'Tu medio de pago fue rechazado. Intenta con otro.',
  'error.gateway_rejected': 'La pasarela rechazó el pago. Intenta con otro medio.',
  'error.invalid_state_transition': 'Esta sesión de pago ya fue resuelta.',
  'error.currency_not_supported': 'La divisa del cobro no está habilitada.',
  'error.amount_below_minimum': 'El monto es menor al mínimo permitido.',
  'error.rate_limited': 'Demasiados intentos. Espera unos segundos y reintenta.',
  'error.gateway_unavailable': 'La pasarela no está disponible. Intenta en unos minutos.',
  'error.internal_error': 'Tuvimos un problema interno. Intenta nuevamente.',
  'error.network_error': 'No pudimos conectarnos. Revisa tu conexión y reintenta.',
  'error.session_invalid': 'No pudimos iniciar la sesión de pago. Vuelve a intentarlo.',
  'error.payment_failed': 'El pago no se pudo completar.',
  'error.intent_expired': 'La sesión de pago expiró.',
  'error.embed_origin_denied': 'Este checkout no está autorizado en este sitio.',
  'error.inscription_lost':
    'No pudimos retomar la inscripción de tu tarjeta. Vuelve a inscribirla para completar el pago.',
  'error.unknown': 'Algo salió mal. Intenta nuevamente.',

  'route.not_found.title': 'Página no disponible',
  'route.not_found.body': 'Esta dirección no corresponde a una sesión de pago.',
} satisfies Record<string, string>;

/** Claves validas del diccionario; `en.ts` debe cubrirlas todas. */
export type MessageKey = keyof typeof esCL;
